import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { parseApiDate } from '../tracking-format.util';
import type { ReportEvent } from '../tracking-demo.models';

/** live_call_events type written when the setup form refuses a website at Join. */
const WEBSITE_REJECTED = 'setup.website_rejected';

/** Plain-English label for the server's reason tag (website_check.diagnose). */
const REASON_LABELS: Record<string, string> = {
  dns_or_private: 'Domain does not resolve (or points to a private IP)',
  ConnectTimeout: 'Timed out connecting (5 s)',
  ReadTimeout: 'Site too slow to answer (5 s)',
  ConnectError: 'Connection refused / failed',
};

/** One refused Join attempt, ready to render. */
export interface WebsiteRejectionRow {
  time: string;
  check: 'format' | 'unreachable' | string;
  website: string;
  reason: string;
}

/** Picks this session's website rejections out of the report's event log, oldest first. */
export function websiteRejections(events: ReportEvent[] | null | undefined): WebsiteRejectionRow[] {
  return (events || [])
    .filter((e) => e.type === WEBSITE_REJECTED)
    .map((e) => {
      const p = e.payload || {};
      const d = parseApiDate(e.at);
      const raw = String(p.reason || '');
      const http = /^http_(\d{3})$/.exec(raw);
      return {
        time: d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—',
        check: String(p.check || ''),
        website: String(p.website || ''),
        reason: p.check === 'format'
          ? 'Not a valid website address'
          : http ? `Site answered HTTP ${http[1]}` : REASON_LABELS[raw] || raw || '—',
      };
    });
}

/**
 * Insights-tab card: every Join the setup form refused over the website field —
 * which check (format vs. server reachability) and why — so a visitor who
 * "clicked Join six times" is a list, not a guess from dead clicks.
 */
@Component({
  selector: 'app-website-rejections',
  standalone: true,
  host: { class: 'block shrink-0' },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (rows.length) {
      <div class="mb-4 shrink-0 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-3">
        <div class="text-[11px] font-medium text-amber-700 dark:text-amber-300">
          Website rejected at Join · {{ rows.length }} {{ rows.length === 1 ? 'attempt' : 'attempts' }}
        </div>
        <table class="mt-2 w-full text-left text-xs">
          <thead class="text-[10px] uppercase tracking-wide text-slate-400">
            <tr><th class="py-1 pr-3 font-medium">Time</th><th class="py-1 pr-3 font-medium">Check</th>
              <th class="py-1 pr-3 font-medium">Website entered</th><th class="py-1 font-medium">Why</th></tr>
          </thead>
          <tbody class="text-slate-700 dark:text-gray-200">
            @for (r of rows; track $index) {
              <tr class="border-t border-amber-100 dark:border-amber-500/20">
                <td class="py-1 pr-3 tabular-nums">{{ r.time }}</td>
                <td class="py-1 pr-3">{{ r.check === 'format' ? 'Format' : 'Unreachable' }}</td>
                <td class="py-1 pr-3 break-all font-mono">{{ r.website || '(blank)' }}</td>
                <td class="py-1">{{ r.reason }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  `,
})
export class WebsiteRejectionsComponent {
  rows: WebsiteRejectionRow[] = [];

  @Input() set events(value: ReportEvent[] | null | undefined) {
    this.rows = websiteRejections(value);
  }
}
